'use client'

/**
 * Coordinates landing-page section tracking with programmatic scrolls.
 *
 * Clicking a nav link glides the page past every section between the current
 * position and the target, so each one crosses the visibility threshold and
 * would report a view the user never had. Callers announce those scrolls here;
 * `TrackedLandingSection` stays silent while one is in flight and then checks
 * which section actually landed on screen.
 */

/** How often the scroll position is sampled while waiting for the scroll to stop. */
const SETTLE_POLL_MS = 100
/** Consecutive unchanged samples before the scroll counts as finished. */
const SETTLE_STABLE_SAMPLES = 3
/** Hard stop, so a scroll that never rests (user takes over) cannot mute tracking. */
const SETTLE_TIMEOUT_MS = 3000

let scrolling = false
let startedAt = 0
let pollTimer: number | undefined
let hashWatcherAttached = false
const settledListeners = new Set<() => void>()

export function isProgrammaticScrollActive() {
  return scrolling
}

/**
 * Marks the start of a programmatic scroll and watches for it to come to rest.
 * Calling this again while one is in flight extends the wait.
 */
export function beginProgrammaticScroll() {
  if (typeof window === 'undefined') return

  scrolling = true
  startedAt = Date.now()
  if (pollTimer !== undefined) return

  let lastY = window.scrollY
  let stableSamples = 0

  pollTimer = window.setInterval(() => {
    const y = window.scrollY
    if (y === lastY) {
      stableSamples += 1
    } else {
      lastY = y
      stableSamples = 0
    }

    if (stableSamples >= SETTLE_STABLE_SAMPLES || Date.now() - startedAt > SETTLE_TIMEOUT_MS) {
      endProgrammaticScroll()
    }
  }, SETTLE_POLL_MS)
}

function endProgrammaticScroll() {
  if (pollTimer !== undefined) {
    window.clearInterval(pollTimer)
    pollTimer = undefined
  }
  scrolling = false
  settledListeners.forEach((listener) => listener())
}

/** Smooth-scrolls to a document offset without reporting the sections passed on the way. */
export function scrollToLandingOffset(top: number) {
  beginProgrammaticScroll()
  window.scrollTo({ top, behavior: 'smooth' })
}

export function onProgrammaticScrollSettled(listener: () => void) {
  attachHashWatcher()
  settledListeners.add(listener)
  return () => {
    settledListeners.delete(listener)
  }
}

/**
 * Plain `#section` links scroll natively, and `globals.css` sets
 * `scroll-behavior: smooth`, so those jumps sweep past sections too.
 */
function attachHashWatcher() {
  if (hashWatcherAttached || typeof window === 'undefined') return
  hashWatcherAttached = true
  window.addEventListener('hashchange', beginProgrammaticScroll)
}
